import { CoreApiVersionPath, RosaCoreApiService } from '@/rosa';
import type { ColorPaletteConfiguration } from '@/ui/color-picker/ui-color-picker.component';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { CardShellConfig } from '../../data-viz/data-viz-widgets/shells/card/card-shell';
import {
    BusinessIntelligenceDataSourceCode,
    type BusinessIntelligenceDimensionCode,
    type BusinessIntelligenceFilter,
    BusinessIntelligenceFilterOperator,
    type BusinessIntelligenceMeasureCode,
    type BusinessIntelligenceRepresentation,
    type BusinessIntelligenceSortCriterion
} from '../../interfaces/BusinessIntelligence.interface';
import {
    BusinessIntelligenceComboChartMeasureChartType,
    type BusinessIntelligenceMemberConfiguration,
    BusinessIntelligenceRepresentationCode
} from '../../interfaces/BusinessIntelligenceRepresentation.interface';
import type { TailoredDashboardId } from '../../interfaces/Dashboard.interface';
import {
    type BasicWidgetCode,
    type BasicWidgetConfig,
    type BusinessIntelligenceDimensionConfiguration,
    type BusinessIntelligenceShellCode,
    type BusinessIntelligenceWidgetConfiguration,
    type LegacyWidgetConfig,
    type TailoredTemplateWidgetConfigurationParams,
    type TailoredWidget,
    type TailoredWidgetConfig,
    type TailoredWidgetId,
    type TailoredWidgetScopeType,
    type TailoredWidgetTemplate,
    type TailoredWidgetTemplateId,
    type TailoredWidgetToCreate,
    type TailoredWidgetToEdit,
    TailoredWidgetType,
    type TemplateWidgetConfig,
    type TemplateWidgetConfiguration
} from '../../interfaces/TailoredWidget.interface';
import type { LegacyBackendWidgetConfig } from '../../interfaces/Widget.interface';
import { LegacyDashboardsProvider } from '../dashboards/legacy-dashboards.provider';
import type { LegacyWidgetCode } from '../rights.constants';

interface CoreLegacyBusinessIntelligenceWidgetConfiguration {
    dataSource: {
        dataSourceCode: BusinessIntelligenceDataSourceCode;
        filters: BusinessIntelligenceFilter[];
        measureCodes: BusinessIntelligenceMeasureCode[];
        dimensions: BusinessIntelligenceDimensionConfiguration[];
    };
    representation:
        | {
              representationCode: BusinessIntelligenceRepresentationCode.HISTOGRAM;
              representationConfig: {
                  xAxis: BusinessIntelligenceDimensionCode;
                  series: BusinessIntelligenceDimensionCode;
                  values: BusinessIntelligenceMeasureCode;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.LINE_CHART;
              representationConfig: {
                  xAxis: BusinessIntelligenceDimensionCode;
                  series: BusinessIntelligenceDimensionCode;
                  values: BusinessIntelligenceMeasureCode[];
                  cumulativeValues?: BusinessIntelligenceMeasureCode[];
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.PIE_CHART;
              representationConfig: {
                  values: BusinessIntelligenceMeasureCode;
                  segmentation: BusinessIntelligenceDimensionCode;
              };
          };
    shell: {
        shellCode: BusinessIntelligenceShellCode.CARD;
        shellConfig: CardShellConfig;
    };
}

interface CoreBusinessIntelligenceWidgetConfiguration {
    dataSource: {
        dataSourceCode: BusinessIntelligenceDataSourceCode;
        filters: BusinessIntelligenceFilter[];
    };
    representation:
        | {
              representationCode: BusinessIntelligenceRepresentationCode.BAR_CHART;
              representationConfig: {
                  categorization: BusinessIntelligenceDimensionConfiguration;
                  segmentation: BusinessIntelligenceDimensionConfiguration;
                  measure: { measureCode: BusinessIntelligenceMeasureCode };
                  sortCriteria: BusinessIntelligenceSortCriterion[];
                  limit?: number;
                  shouldZeroBeDisplayed: boolean;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.HISTOGRAM;
              representationConfig: {
                  xAxis: BusinessIntelligenceDimensionConfiguration;
                  segmentation: BusinessIntelligenceDimensionConfiguration;
                  measure: { measureCode: BusinessIntelligenceMeasureCode };
                  sortCriteria: BusinessIntelligenceSortCriterion[];
                  limit: number | null;
                  shouldZeroBeDisplayed: boolean;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.LINE_CHART;
              representationConfig: {
                  xAxis: BusinessIntelligenceDimensionConfiguration;
                  segmentation: BusinessIntelligenceDimensionConfiguration;
                  measures: (
                      | {
                            measureCode: BusinessIntelligenceMeasureCode;
                            isCumulative: boolean;
                            color: ColorPaletteConfiguration | null;
                        }
                      | {
                            measureCode: BusinessIntelligenceMeasureCode;
                            isCumulative: boolean;
                            chartType: BusinessIntelligenceComboChartMeasureChartType;
                            columnStackingGroup: number | null;
                            color: ColorPaletteConfiguration | null;
                        }
                  )[];
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.PIE_CHART;
              representationConfig: {
                  measure: { measureCode: BusinessIntelligenceMeasureCode };
                  segmentation: BusinessIntelligenceDimensionConfiguration;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.SINGLE_VALUE;
              representationConfig: {
                  measure: { measureCode: BusinessIntelligenceMeasureCode };
                  caption: string;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.SCATTER_CHART;
              representationConfig: {
                  xAxis: { measureCode: BusinessIntelligenceMeasureCode };
                  yAxis: { measureCode: BusinessIntelligenceMeasureCode };
                  color: ColorPaletteConfiguration | null;
                  pointSize: { measureCode: BusinessIntelligenceMeasureCode };
                  groupBy: BusinessIntelligenceDimensionConfiguration;
                  segmentation: BusinessIntelligenceDimensionConfiguration;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.TABLE;
              representationConfig: {
                  members: BusinessIntelligenceMemberConfiguration[];
                  sortCriteria: BusinessIntelligenceSortCriterion[];
                  limit: number;
                  displayLinesWithNullValues: boolean;
              };
          }
        | {
              representationCode: BusinessIntelligenceRepresentationCode.PIVOT_TABLE;
              representationConfig: {
                  lines: BusinessIntelligenceDimensionConfiguration[];
                  pivot: BusinessIntelligenceDimensionConfiguration;
                  measure: { measureCode: BusinessIntelligenceMeasureCode };
                  sortCriteria: BusinessIntelligenceSortCriterion[];
              };
          };
    shell: {
        shellCode: BusinessIntelligenceShellCode.CARD;
        shellConfig: CardShellConfig;
    };
}

interface TailoredWidgetCore {
    widgetId: TailoredWidgetId;
    name: string;
    code: string;
    type: TailoredWidgetScopeType;
    isInCatalog: boolean;
    canBeMonoSite: boolean;
    canBeMultiSite: boolean;
    thumbnailUrl: string;
    configuration?:
        | CoreBusinessIntelligenceWidgetConfiguration
        | CoreLegacyBusinessIntelligenceWidgetConfiguration
        | TemplateWidgetConfiguration
        | LegacyWidgetConfigurationCore
        | BasicWidgetConfigurationCore;
    widgetTemplateId?: TailoredWidgetTemplateId;
    widgetTemplateParams?: TailoredTemplateWidgetConfigurationParams;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
}

// To remove LegacyWidgetConfigurationCore interface when the new architecture is ready

interface LegacyWidgetConfigurationCore {
    isLegacyWidget: boolean;
    legacyWidgetCode: LegacyWidgetCode;
    legacyConfig: LegacyBackendWidgetConfig<any, any>;
}
interface BasicWidgetConfigurationCore {
    isLegacyWidget: boolean;
    widgetCode: BasicWidgetCode;
    config: any;
}

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreTailoredWidgetsProvider {
    private _rosaCoreApiService = inject(RosaCoreApiService);
    private _legacyDashboardsProvider = inject(LegacyDashboardsProvider);

    createWidget$(widgetToCreate: TailoredWidgetToCreate): Observable<TailoredWidgetId> {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V4}widgets`, widgetToCreate);
    }

    editWidget$(widgetId: TailoredWidgetId, widgetToEdit: TailoredWidgetToEdit): Observable<void> {
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V4}widgets/${widgetId}`, widgetToEdit);
    }

    deleteWidget$(widgetId: TailoredWidgetId): Observable<void> {
        return this._rosaCoreApiService.delete(`${CoreApiVersionPath.V4}widgets/${widgetId}`);
    }

    fetchWidgets$(): Observable<TailoredWidget[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V4}widgets/me`).pipe(
            map(tailoredWidgetCores => {
                const tailoredWidgets = (tailoredWidgetCores as TailoredWidgetCore[]).map(tailoredWidgetCore =>
                    this.mapTailoredWidgetCoreToTailoredWidget(tailoredWidgetCore)
                );
                return tailoredWidgets;
            })
        );
    }

    fetchWidgetTemplates$(): Observable<TailoredWidgetTemplate[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V4}widgets/templates`);
    }

    fetchDashboardsByWidgetId$(widgetId: TailoredWidgetId): Observable<TailoredDashboardId[]> {
        return this._rosaCoreApiService.get(`${CoreApiVersionPath.V4}dashboards/widgets/${widgetId}`);
    }

    private mapTailoredWidgetCoreToTailoredWidget(tailoredWidgetCore: TailoredWidgetCore): TailoredWidget {
        const tailoredWidget: TailoredWidget = {
            widgetId: tailoredWidgetCore.widgetId,
            name: tailoredWidgetCore.name,
            code: tailoredWidgetCore.code,
            type: tailoredWidgetCore.type,
            isInCatalog: tailoredWidgetCore.isInCatalog,
            canBeMonoSite: tailoredWidgetCore.canBeMonoSite,
            canBeMultiSite: tailoredWidgetCore.canBeMultiSite,
            thumbnailUrl: tailoredWidgetCore.thumbnailUrl,
            minWidth: tailoredWidgetCore.minWidth,
            maxWidth: tailoredWidgetCore.maxWidth,
            minHeight: tailoredWidgetCore.minHeight,
            maxHeight: tailoredWidgetCore.maxHeight,
            configuration: null as unknown as TailoredWidgetConfig
        };
        if (isNotNullish(tailoredWidgetCore.widgetTemplateId)) {
            const widgetTemplate: TemplateWidgetConfig = {
                widgetType: TailoredWidgetType.TEMPLATE_WIDGET,
                tailoredWidgetTemplateConfiguration: {
                    templateWidgetConfiguration: tailoredWidgetCore.configuration,
                    widgetTemplateId: tailoredWidgetCore.widgetTemplateId,
                    templateWidgetConfigurationParams: tailoredWidgetCore.widgetTemplateParams ?? null
                }
            };
            tailoredWidget.configuration = widgetTemplate;
        } else if (tailoredWidgetCore.configuration && (tailoredWidgetCore.configuration as LegacyWidgetConfigurationCore).isLegacyWidget) {
            // To remove legacy widget type when the new architecture is ready
            const legacyWidgetConfig: LegacyWidgetConfigurationCore = tailoredWidgetCore.configuration as LegacyWidgetConfigurationCore;
            const legacyWidget: LegacyWidgetConfig = {
                widgetType: TailoredWidgetType.LEGACY_WIDGET,
                legacyWidgetConfiguration: {
                    legacyWidgetCode: legacyWidgetConfig.legacyWidgetCode,
                    legacyConfig: this._legacyDashboardsProvider.getLegacyFrontEndWidgetConfig(legacyWidgetConfig.legacyConfig)
                }
            };
            tailoredWidget.configuration = legacyWidget;
        } else if (
            tailoredWidgetCore.configuration &&
            isNotNullish((tailoredWidgetCore.configuration as BasicWidgetConfigurationCore).isLegacyWidget) &&
            !(tailoredWidgetCore.configuration as BasicWidgetConfigurationCore).isLegacyWidget
        ) {
            // to merge with legacy widget
            const basicWidgetConfigurationCore: any = tailoredWidgetCore.configuration as BasicWidgetConfigurationCore;
            const basicWidget: BasicWidgetConfig = {
                widgetType: TailoredWidgetType.BASIC_WIDGET,
                basicWidgetConfiguration: {
                    code: basicWidgetConfigurationCore.widgetCode,
                    basicWidgetSetting: basicWidgetConfigurationCore.config
                }
            };
            tailoredWidget.configuration = basicWidget;
        } else if (isNotNullish(tailoredWidgetCore.configuration)) {
            tailoredWidget.configuration = {
                widgetType: TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET,
                businessIntelligenceConfiguration:
                    this.mapCoreBusinessIntelligenceWidgetConfigurationToBusinessIntelligenceWidgetConfiguration(
                        tailoredWidgetCore.configuration as
                            | CoreBusinessIntelligenceWidgetConfiguration
                            | CoreLegacyBusinessIntelligenceWidgetConfiguration
                    )
            };
        }

        return tailoredWidget;
    }

    private mapLegacyFilterCode(filters: BusinessIntelligenceFilter[]): BusinessIntelligenceFilter[] {
        if (filters && filters.length) {
            return filters
                .map(filter => {
                    if (filter.operator === ('inSelectPeriod' as BusinessIntelligenceFilterOperator)) {
                        return {
                            ...filter,
                            operator: BusinessIntelligenceFilterOperator.IS_PERIOD
                        } as BusinessIntelligenceFilter;
                    }
                    return filter;
                })
                .filter(f => f.memberCode !== `AccessContext.contextId`);
        }
        return filters;
    }

    private mapCoreBusinessIntelligenceWidgetConfigurationToBusinessIntelligenceWidgetConfiguration(
        configToMap: CoreBusinessIntelligenceWidgetConfiguration | CoreLegacyBusinessIntelligenceWidgetConfiguration
    ): BusinessIntelligenceWidgetConfiguration {
        if (this.isLegacyCoreBusinessIntelligenceWidgetConfiguration(configToMap)) {
            return {
                dataSource: {
                    dataSourceCode: this.mapLegacyDatasourceCode(configToMap.dataSource.dataSourceCode),
                    filters: this.mapLegacyFilterCode(configToMap.dataSource.filters)
                },
                shell: configToMap.shell,
                representation:
                    this.generateBusinessIntelligenceRepresentationFromLegacyCoreBusinessIntelligenceWidgetConfiguration(configToMap)
            };
        } else {
            return {
                ...configToMap,
                dataSource: {
                    dataSourceCode: this.mapLegacyDatasourceCode(configToMap.dataSource.dataSourceCode),
                    filters: this.mapLegacyFilterCode(configToMap.dataSource.filters)
                },
                representation: this.generateBusinessIntelligenceRepresentationFromCoreBusinessIntelligenceWidgetConfiguration(configToMap)
            };
        }
    }

    private generateBusinessIntelligenceRepresentationFromCoreBusinessIntelligenceWidgetConfiguration(
        configToMap: CoreBusinessIntelligenceWidgetConfiguration
    ): BusinessIntelligenceRepresentation {
        const backendRepresentation = configToMap.representation;
        switch (backendRepresentation.representationCode) {
            case BusinessIntelligenceRepresentationCode.BAR_CHART:
            case BusinessIntelligenceRepresentationCode.PIE_CHART:
            case BusinessIntelligenceRepresentationCode.SINGLE_VALUE:
            case BusinessIntelligenceRepresentationCode.TABLE:
            case BusinessIntelligenceRepresentationCode.PIVOT_TABLE:
                return backendRepresentation;
            case BusinessIntelligenceRepresentationCode.HISTOGRAM:
                return {
                    ...backendRepresentation,
                    representationConfig: {
                        ...backendRepresentation.representationConfig,
                        click: null
                    }
                };
            case BusinessIntelligenceRepresentationCode.SCATTER_CHART:
                return {
                    ...backendRepresentation,
                    representationConfig: {
                        ...backendRepresentation.representationConfig,
                        click: null
                    }
                };
            case BusinessIntelligenceRepresentationCode.LINE_CHART:
                return {
                    representationCode: BusinessIntelligenceRepresentationCode.LINE_CHART,
                    representationConfig: {
                        ...backendRepresentation.representationConfig,
                        measures: backendRepresentation.representationConfig.measures.map(measure => {
                            if (!('chartType' in measure)) {
                                return {
                                    ...measure,
                                    chartType: BusinessIntelligenceComboChartMeasureChartType.LINE,
                                    columnStackingGroup: null
                                };
                            } else {
                                return measure;
                            }
                        }),
                        displayZeros: null
                    }
                };
        }
    }

    private mapLegacyDatasourceCode(legacyDataSource: BusinessIntelligenceDataSourceCode): BusinessIntelligenceDataSourceCode {
        switch (legacyDataSource) {
            case BusinessIntelligenceDataSourceCode.LEGACY_COST_VAT_INCL:
            case BusinessIntelligenceDataSourceCode.LEGACY_COST_VAT_EXCL:
            case BusinessIntelligenceDataSourceCode.LEGACY_PRIMARY_CONSUMPTION:
            case BusinessIntelligenceDataSourceCode.LEGACY_ENERGY_CONSUMPTION:
            case BusinessIntelligenceDataSourceCode.LEGACY_WATER_CONSUMPTION:
            case BusinessIntelligenceDataSourceCode.LEGACY_CO2:
            case BusinessIntelligenceDataSourceCode.LEGACY_CO2_RSE:
                return BusinessIntelligenceDataSourceCode.CONSUMPTION;
            default:
                return legacyDataSource;
        }
    }

    private isLegacyCoreBusinessIntelligenceWidgetConfiguration(
        config: CoreBusinessIntelligenceWidgetConfiguration | CoreLegacyBusinessIntelligenceWidgetConfiguration
    ): config is CoreLegacyBusinessIntelligenceWidgetConfiguration {
        return Boolean(
            config && config.dataSource && (config as CoreLegacyBusinessIntelligenceWidgetConfiguration).dataSource.measureCodes
        );
    }

    private generateBusinessIntelligenceRepresentationFromLegacyCoreBusinessIntelligenceWidgetConfiguration(
        legacyConfig: CoreLegacyBusinessIntelligenceWidgetConfiguration
    ): BusinessIntelligenceRepresentation {
        const legacyRepresentation = legacyConfig.representation;
        switch (legacyRepresentation.representationCode) {
            case BusinessIntelligenceRepresentationCode.HISTOGRAM: {
                return {
                    representationCode: BusinessIntelligenceRepresentationCode.HISTOGRAM,
                    representationConfig: {
                        xAxis: legacyConfig.dataSource.dimensions.find(
                            dim => dim.dimensionCode === legacyRepresentation.representationConfig.xAxis
                        )!,
                        segmentation: legacyRepresentation.representationConfig.series
                            ? (legacyConfig.dataSource.dimensions.find(
                                  dim => dim.dimensionCode === legacyRepresentation.representationConfig.series
                              ) ?? null)
                            : null,
                        click: null,
                        measure: {
                            measureCode: legacyRepresentation.representationConfig.values
                        },
                        sortCriteria: [],
                        limit: null,
                        shouldZeroBeDisplayed: false
                    }
                };
            }
            case BusinessIntelligenceRepresentationCode.PIE_CHART: {
                return {
                    representationCode: BusinessIntelligenceRepresentationCode.PIE_CHART,
                    representationConfig: {
                        segmentation: legacyRepresentation.representationConfig.segmentation
                            ? (legacyConfig.dataSource.dimensions.find(
                                  dim => dim.dimensionCode === legacyRepresentation.representationConfig.segmentation
                              ) ?? null)
                            : null,
                        measure: {
                            measureCode: legacyRepresentation.representationConfig.values
                        },
                        areDataLabelsActivated: false
                    }
                };
            }
            case BusinessIntelligenceRepresentationCode.LINE_CHART: {
                return {
                    representationCode: BusinessIntelligenceRepresentationCode.LINE_CHART,
                    representationConfig: {
                        measures: legacyRepresentation.representationConfig.values.map(legacyValue => {
                            return {
                                measureCode: legacyValue,
                                isCumulative: false,
                                chartType: BusinessIntelligenceComboChartMeasureChartType.LINE,
                                columnStackingGroup: null,
                                color: null
                            };
                        }),
                        segmentation: legacyRepresentation.representationConfig.series
                            ? (legacyConfig.dataSource.dimensions.find(
                                  dim => dim.dimensionCode === legacyRepresentation.representationConfig.series
                              ) ?? null)
                            : null,
                        xAxis: legacyConfig.dataSource.dimensions.find(
                            dim => dim.dimensionCode === legacyRepresentation.representationConfig.xAxis
                        )!,
                        displayZeros: null
                    }
                };
            }
            default:
                console.error('Enable to map legacy representation', legacyConfig ? JSON.stringify(legacyConfig) : null);
                return null!;
        }
    }
}
